import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

// CONTROL PAGE (owner 2026-10-13): every parameter, rule, strategy switch, indicator and AI setting the
// engine uses — view, change, reset. Changes apply to the running engine at once (paper AND live) and
// survive restarts. Admin only.
const fmt = (p, v) => {
  if (v === null || v === undefined) return '—';
  if (p.type === 'bool') return v ? 'ON' : 'OFF';
  if (p.type === 'time') return String(v);
  const n = Number(v) * (p.scale || 1);
  return `${Number(n.toFixed(4))}${p.unit ? ' ' + p.unit : ''}`;
};

function Row({ p, onSaved }) {
  const shown = (v) => (p.type === 'bool' || p.type === 'time') ? v : Number((Number(v) * (p.scale || 1)).toFixed(4));
  const [val, setVal] = useState(shown(p.value));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setVal(shown(p.value)); }, [p.value]);   // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = p.type === 'bool' ? false : String(val) !== String(shown(p.value));

  const save = async (raw) => {
    setBusy(true); setMsg(null);
    try {
      const value = (p.type === 'bool' || p.type === 'time') ? raw : Number(raw) / (p.scale || 1);
      const r = await api.setControl(p.key, value);
      if (r.status === 'success') { setMsg({ ok: true, t: 'saved' }); onSaved(); }
      else setMsg({ ok: false, t: r.message || 'not saved' });
    } catch (e) { setMsg({ ok: false, t: 'not saved — server not reachable' }); }
    setBusy(false);
  };
  const reset = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.resetControl(p.key);
      if (r.status === 'success') { setMsg({ ok: true, t: 'back to default' }); onSaved(); }
      else setMsg({ ok: false, t: r.message || 'failed' });
    } catch (e) { setMsg({ ok: false, t: 'failed' }); }
    setBusy(false);
  };

  return (
    <div className="ctl-row">
      <div className="ctl-label">
        <div>
          {p.label}
          {p.changed && <span className="ctl-badge">edited</span>}
        </div>
        <div className="ctl-note">
          {p.note ? p.note + ' · ' : ''}default {fmt(p, p.default)}
          {p.type !== 'bool' && p.type !== 'time' ? ` · allowed ${Number((p.min * (p.scale || 1)).toFixed(4))}–${Number((p.max * (p.scale || 1)).toFixed(4))}` : ''}
        </div>
      </div>
      <div className="ctl-input">
        {p.type === 'bool' ? (
          <button className={`ctl-toggle ${p.value ? 'on' : ''}`} disabled={busy}
                  onClick={() => save(!p.value)} aria-pressed={!!p.value}>
            {p.value ? 'ON' : 'OFF'}
          </button>
        ) : (
          <>
            <input type={p.type === 'time' ? 'time' : 'number'} value={val} step="any"
                   onChange={e => setVal(e.target.value)}
                   onKeyDown={e => { if (e.key === 'Enter' && dirty) save(val); }} />
            {p.unit && p.type !== 'time' && <span className="ctl-unit">{p.unit}</span>}
            <button className="btn btn-start ctl-btn" disabled={!dirty || busy} onClick={() => save(val)}>Save</button>
          </>
        )}
        {p.changed && <button className="btn btn-paper ctl-btn" disabled={busy} onClick={reset} title="Back to default">↺</button>}
      </div>
      {msg && <div className={`ctl-msg ${msg.ok ? 'ok' : 'bad'}`}>{msg.t}</div>}
    </div>
  );
}

export default function Control() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [onlyEdited, setOnlyEdited] = useState(false);
  const [openG, setOpenG] = useState({});   // which groups are expanded (411 settings — collapsed by default)

  const load = async () => {
    try {
      const r = await api.getControl();
      if (r.status === 'success') { setData(r); setErr(''); }
      else setErr(r.message || 'Could not load settings');
    } catch (e) { if (!data) setErr('Could not load settings — server not reachable'); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const out = [];
    const qq = q.trim().toLowerCase();
    (data?.params || []).forEach(p => {
      if (onlyEdited && !p.changed) return;
      if (qq && !(`${p.label} ${p.note} ${p.group}`.toLowerCase().includes(qq))) return;
      let g = out.find(x => x.name === p.group);
      if (!g) { g = { name: p.group, items: [] }; out.push(g); }
      g.items.push(p);
    });
    return out;
  }, [data, q, onlyEdited]);

  const resetAll = async () => {
    if (!window.confirm('Put EVERY setting back to its default?')) return;
    try { await api.resetControl('*'); } catch (e) { /* reload shows the truth */ }
    load();
  };

  const applyGolden = async () => {
    let note = '';
    let vals = {};
    try {
      const r = await api.getPresets();
      note = r?.presets?.golden?.note || '';
      vals = r?.presets?.golden?.values || {};
    } catch (e) { /* the confirm still explains */ }
    const lines = Object.entries(vals).map(([k, v]) => {
      const p = (data?.params || []).find(x => x.key === k);
      return `• ${p ? p.label : k}: ${p ? fmt(p, p.value) : '?'} → ${p ? fmt(p, v) : v}`;
    }).join('\n');
    if (!window.confirm(`Apply the GOLDEN preset?\n\n${note}\n\n${lines}\n\nApplies to paper AND live at once. You can reset any setting later.`)) return;
    try {
      const r = await api.applyPreset('golden');
      if (r.status !== 'success') window.alert(r.message || 'Preset not applied');
    } catch (e) { window.alert('Preset not applied — server not reachable'); }
    load();
  };

  if (err && !data) return <div className="card full-width"><p style={{ color: '#ff6b6b', padding: '1rem' }}>{err}</p></div>;
  if (!data) return <div className="card full-width"><p style={{ color: '#888', padding: '1rem' }}>Loading settings…</p></div>;
  const edited = (data.params || []).filter(p => p.changed).length;

  return (
    <div className="control-page">
      <div className="card full-width">
        <div className="card-header">
          <span className="card-title">⚙️ Control — rules, strategies, indicators, AI</span>
          <span style={{ color: '#888', fontSize: '0.75rem' }}>{data.params.length} settings · {edited} edited</span>
        </div>
        <div className="ctl-warn">
          Changes apply to the running engine <b>immediately</b>, on <b>paper and live</b> alike, and stay after restarts.
          Change one thing at a time and watch the result.
        </div>
        <div className="ctl-tools">
          <input placeholder="Search settings…" value={q} onChange={e => setQ(e.target.value)} />
          <label><input type="checkbox" checked={onlyEdited} onChange={e => setOnlyEdited(e.target.checked)} /> only edited</label>
          <button className="btn btn-paper" onClick={resetAll} disabled={!edited}>Reset all to default</button>
          <button className="btn btn-start" onClick={applyGolden}>⭐ Apply GOLDEN preset</button>
        </div>
      </div>

      {groups.map(g => {
        const isOpen = !!openG[g.name] || !!q.trim() || onlyEdited;
        const nEdited = g.items.filter(p => p.changed).length;
        return (
          <div className="card full-width ctl-group" key={g.name}>
            <button className="ctl-ghead" aria-expanded={isOpen}
                    onClick={() => setOpenG(o => ({ ...o, [g.name]: !o[g.name] }))}>
              <span className="card-title">{isOpen ? '▾' : '▸'} {g.name}</span>
              <span className="ctl-gcount">{g.items.length} settings{nEdited ? ` · ${nEdited} edited` : ''}</span>
            </button>
            {isOpen && g.items.map(p => <Row key={p.key} p={p} onSaved={load} />)}
          </div>
        );
      })}
      {groups.length === 0 && <div className="card full-width"><p style={{ color: '#888', padding: '1rem' }}>No setting matches.</p></div>}

      <div className="card full-width">
        <div className="card-header"><span className="card-title">Recent changes</span></div>
        {(data.audit || []).length === 0 ? <p style={{ color: '#888', fontSize: '0.85rem' }}>No changes yet.</p> : (
          (data.audit || []).map((a, i) => {
            const p = (data.params || []).find(x => x.key === a.key) || { type: 'float', scale: 1, unit: '', label: a.key };
            return (
              <div key={i} className="ctl-audit">
                <span style={{ color: '#666' }}>{a.ts}</span> · {a.who} · <b>{p.label}</b>: {fmt(p, a.old)} → <span style={{ color: '#00d9ff' }}>{fmt(p, a.new)}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
